{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2018 - 2021                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.LocalFiles;

interface

{$DEFINE NOPP}

uses
  Classes, SysUtils, JS, Web, jsDelphiSystem;

type
  TOpenTextFileProc = reference to procedure(AText: string);
  TOpenBinaryFileProc = reference to procedure(AValue: TJSArrayBuffer);

  TSaveFileProc = reference to procedure;

  TFileFilterItem = class(TCollectionItem)
  private
    FExtensions: TStringList;
    FMimeType: string;
    FDescription: string;
    procedure SetExtensions(const Value: TStrings);
    function GetExtensions: TStrings;
  public
    constructor Create(ACollection: TCollection); override;
    destructor Destroy; override;
  published
    property Description: string read FDescription write FDescription;
    property MIMEType: string read FMimeType write FMimeType;
    property Extensions: TStrings read GetExtensions write SetExtensions;
  end;

  TFileFilter = class(TOwnedCollection)
  private
    function GetItems(Index: integer): TFileFilterItem;
    procedure SetItems(Index: integer; const Value: TFileFilterItem);
  protected
    function GetFilterString: string;
    function GetFilterObject: TJSObject;
    function HasAllFiles: boolean;
  public
    constructor Create(AOwner: TPersistent); reintroduce;
    function Add: TFileFilterItem; reintroduce; overload;
    function Add(ADescription: string; AMIMEType: string; AExtensions: string): TFileFilterItem; overload;
    function Insert(Index: integer): TFileFilterItem; reintroduce;
    property Items[Index: integer]: TFileFilterItem read GetItems write SetItems;
  end;

  TTextFile = class(TComponent)
  private
    FFileHandle: JSValue;
    FText: string;
    FOnFileSave: TNotifyEvent;
    FOnFileOpen: TNotifyEvent;
    FFileName: string;
    FFilter: TFileFilter;
  protected
    procedure DoOpenFile(AHandle: JSValue; Value: string; AOpenFile: TOpenTextFileProc);
    procedure DoSaveFile(ASaveFile: TSaveFileProc);
    procedure DoSaveAsFile(AHandle: JSValue; ASaveFile: TSaveFileProc);
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    property FileHandle: JSValue read FFileHandle write FFileHandle;
    [async]
    function Open: TJSPromise;
    [async]
    function Save: TJSPromise;
    [async]
    function SaveAs: TJSPromise;
    procedure OpenFile; overload;
    procedure OpenFile(AOpenFile: TOpenTextFileProc); overload;
    procedure SaveFile; overload;
    procedure SaveFile(ASaveFile: TSaveFileProc); overload;
    procedure SaveAsFile; overload;
    procedure SaveAsFile(ASaveFile: TSaveFileProc); overload;
    property Text: string read FText write FText;
    property FileName: string read FFileName;
    property Filter: TFileFilter read FFilter;
  published
    property OnFileOpen: TNotifyEvent read FOnFileOpen write FOnFileOpen;
    property OnFileSave: TNotifyEvent read FOnFileSave write FOnFileSave;
  end;

  TLocalTextFile = class(TTextFile);

  TWebLocalTextFile = class(TTextFile)
  published
    property Filter;
  end;

  TBinaryFile = class(TComponent)
  private
    FFileHandle: JSValue;
    FData: TJSArrayBuffer;
    FOnFileSave: TNotifyEvent;
    FOnFileOpen: TNotifyEvent;
    FFileName: string;
    FFilter: TFileFilter;
  protected
    procedure DoOpenFile(AHandle: JSValue; Value: TJSArrayBuffer; AOpenFile: TOpenBinaryFileProc);
    procedure DoSaveFile(ASaveFile: TSaveFileProc);
    procedure DoSaveAsFile(AHandle: JSValue; ASaveFile: TSaveFileProc);
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    property FileHandle: JSValue read FFileHandle write FFileHandle;
    [async]
    function Open: TJSPromise; overload;
    [async]
    function Open(AFileHandle: JSValue): TJSPromise; overload;
    [async]
    function Save: TJSPromise;
    [async]
    function SaveAs: TJSPromise;
    procedure OpenFile; overload;
    procedure OpenFile(AOpenFile: TOpenBinaryFileProc); overload;
    procedure OpenFile(AFileHandle: JSValue; AOpenFile: TOpenBinaryFileProc); overload;
    procedure SaveFile; overload;
    procedure SaveFile(ASaveFile: TSaveFileProc); overload;
    procedure SaveAsFile; overload;
    procedure SaveAsFile(ASaveFile: TSaveFileProc); overload;
    property Data: TJSArrayBuffer read FData write FData;
    property FileName: string read FFileName;
    property Filter: TFileFilter read FFilter;
    [async]
    function LoadStream(Stream: TCustomMemoryStream): TJSPromise;
    [async]
    function SaveStream(Stream: TCustomMemoryStream): TJSPromise;
  published
    property OnFileOpen: TNotifyEvent read FOnFileOpen write FOnFileOpen;
    property OnFileSave: TNotifyEvent read FOnFileSave write FOnFileSave;
  end;

  TLocalBinaryFile = class(TBinaryFile);

  TWebLocalBinaryFile = class(TBinaryFile)
  published
    property Filter;
  end;

  TFileSystemFileHandle = record
    Kind: string;
    Name: string;
  end;

  TFileSystemFolderHandle = JSValue;

  TFileObject = record
    Name: string;
    Lastmodfieddate: string;
    Size: longint;
    Fileobject: JSValue;
  end;

  TFileSystemFileHandleArray = array of TFileSystemFileHandle;

  TOpenFolderProc = reference to procedure;

  TGetFileProc = reference to procedure(AFileHandle: TFileObject);
  TGetFileHandleProc = reference to procedure(AFileHandle: JSValue);
  TGetFolderProc = reference to procedure(AFolderHandle: TFileSystemFolderHandle);

  TFolder = class(TComponent)
  private
    FFileArray: TFileSystemFileHandleArray;
    FFolder: JSValue;
    FOnFolderOpen: TNotifyEvent;
  protected
    procedure DoGetFile(AFileHandle: JSValue; AProc: TGetFileProc);
    procedure DoGetFileHandle(AFileHandle: JSValue; AProc: TGetFileHandleProc);
    procedure DoListFile(AFile: JSValue);
    procedure DoGetFolder(AFolder: TFileSystemFileHandle);
    procedure DoOpenFolder(AOpenFolder: TOpenFolderProc);
    procedure DoGetFolderHandle(AFolder: TFileSystemFolderHandle; GetFolder: TGetFolderProc);
    procedure DoCreateFolder(const AName: string; AProc: TGetFolderProc);
    procedure DoCreateFile(const AName: string; AProc: TGetFileHandleProc);
  public
    constructor Create(AFolderHandle: JSValue); overload;
    constructor Create(AOwner: TComponent); override; overload;
    function OpenFolder: TJSPromise;
    procedure Open; overload;
    procedure Open(AOpenFolder: TOpenFolderProc); overload;
    [async]
    function CreateFolder(const AName: string): TJSPromise;
    [async]
    function CreateFile(const AName: string): TJSPromise;
    [async]
    function FileHandle(const AName: string): TJSPromise;
    [async]
    function FileObject(const AName: string): TJSPromise;
    [async]
    function Folder(const AName: string): TJSPromise;

    procedure GetFile(const AName: string; GetFile: TGetFileProc);
    procedure GetFileHandle(const AName: string; GetFile: TGetFileHandleProc);
    procedure GetFolder(const AName: string; GetFolder: TGetFolderProc);
    property Files: TFileSystemFileHandleArray read FFileArray;
    property FolderHandle: JSValue read FFolder;
  published
    property OnFolderOpen: TNotifyEvent read FOnFolderOpen write FOnFolderOpen;
  end;

  TLocalFolder = class(TFolder);

  TWebLocalFolder = class(TFolder);

implementation

uses
  WEBLib.Utils;

{ TTextFile }

constructor TTextFile.Create(AOwner: TComponent);
begin
  inherited;
  FFilter := TFileFilter.Create(Self);

  if (1<0) then
  begin
    DoOpenFile(0,'',nil);
    DoSaveFile(nil);
    DoSaveAsFile(0,nil);
  end;

end;

destructor TTextFile.Destroy;
begin
  FFilter.Free;
  inherited;
end;

procedure TTextFile.DoOpenFile(AHandle: JSValue; Value: string; AOpenFile: TOpenTextFileProc);
begin
  FFileHandle := AHandle;
  asm
    this.FFileName = AHandle.name;
  end;
  FText := Value;
  if Assigned(OnFileOpen) then
    OnFileOpen(Self);
  if Assigned(AOpenFile) then
    AOpenFile(Value);
end;

procedure TTextFile.DoSaveAsFile(AHandle: JSValue; ASaveFile: TSaveFileProc);
begin
  FFileHandle := AHandle;
  SaveFile(ASaveFile);
end;

procedure TTextFile.DoSaveFile(ASaveFile: TSaveFileProc);
begin
  if Assigned(OnFileSave) then
    OnFileSave(Self);
  if Assigned(ASaveFile) then
    ASaveFile;
end;

{$HINTS OFF}
function TTextFile.Open: TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      OpenFile(procedure(AText: string)
        begin
          ASuccess(AText);
        end);
    end);
end;

procedure TTextFile.OpenFile(AOpenFile: TOpenTextFileProc);
var
  jo: TJSObject;

begin
  jo := Filter.GetFilterObject;

  asm
    let fileHandle;
    async function asyncCall(afile) {
      [fileHandle] = await window.showOpenFilePicker(jo);
      const file = await fileHandle.getFile();
      const contents = await file.text();
      afile.DoOpenFile(fileHandle, contents, AOpenFile);
      }
    asyncCall(this);
  end;
end;

procedure TTextFile.OpenFile;
begin
  OpenFile(nil);
end;

function TTextFile.Save: TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      SaveFile(procedure
        begin
          ASuccess(true);
        end);
    end);
end;

function TTextFile.SaveAs: TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      SaveAsFile(procedure
        begin
          ASuccess(true);
        end);
    end);
end;

procedure TTextFile.SaveAsFile(ASaveFile: TSaveFileProc);
var
  jo: TJSObject;
begin
  jo := FFilter.GetFilterObject;
  asm
    async function getNewFileHandle(afile, aproc) {
      const options = {
        types: [
         {
          description: 'Text Files',
          accept: { 'text/plain': ['.txt'],
           },
          },
         ],
       };
    const handle = await window.showSaveFilePicker(jo);
    afile.DoSaveAsFile(handle, aproc);
    }
    getNewFileHandle(this, ASaveFile);
  end;
  if 1<0 then
  begin
    DoOpenFile(nil, '', nil);
    DoSaveFile(nil);
    DoSaveAsFile(nil,nil);
  end;
end;
{$HINTS ON}


procedure TTextFile.SaveAsFile;
begin
  SaveAsFile(nil);
end;

procedure TTextFile.SaveFile;
begin
  SaveFile(nil);
end;

procedure TTextFile.SaveFile(ASaveFile: TSaveFileProc);
begin
  if not Assigned(FFileHandle) then
    raise Exception.Create('File handle not assigned');
  asm
    async function writeFile(afile, fileHandle, contents, aproc) {
    // Create a FileSystemWritableFileStream to write to.
    const writable = await fileHandle.createWritable();
    // Write the contents of the file to the stream.
    await writable.write(contents);
    // Close the file and write the contents to disk.
    await writable.close();
    afile.DoSaveFile(aproc);
    }
    writeFile(this, this.FFileHandle, this.FText, ASaveFile);
  end;
end;

{ TFolder }

constructor TFolder.Create(AFolderHandle: JSValue);
begin
  inherited Create;
  FFolder := AFolderHandle;
end;

constructor TFolder.Create(AOwner: TComponent);
var
  fs: TFileSystemFileHandle;
begin
  inherited Create;
  FFolder := nil;

  if (1<0) then
  begin
    DoGetFile(0,nil);
    DoListFile(0);
    DoGetFolder(fs);
    DoOpenFolder(nil);
    DoGetFolderHandle(0,nil);
  end;

  if 1<0 then
    DoGetFileHandle(nil,nil);

end;

procedure TFolder.DoCreateFile(const AName: string; AProc: TGetFileHandleProc);
begin
  asm
    async function MakeFile(afolder,name) {
      const newFileHandle = await afolder.getFileHandle(name, { create: true,} );
      AProc(newFileHandle);
    }
    MakeFile(AName);
  end;
end;

function TFolder.CreateFile(const AName: string): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      DoCreateFile(AName, procedure(AFileHandle: JSValue)
        begin
          ASuccess(AFileHandle);
        end);
    end
  );
end;

procedure TFolder.DoCreateFolder(const AName: string; AProc: TGetFolderProc);
begin
  asm
    async function MakeFolder(afolder,name) {
      const newDirectoryHandle = await afolder.getDirectoryHandle(name, { create: true,} );
      AProc(newDirectoryHandle);
    }
    MakeFolder(AName);
  end;
end;

function TFolder.CreateFolder(const AName: string): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      DoCreateFolder(AName, procedure(AFolderHandle: TFileSystemFolderHandle)
        begin
          ASuccess(AFolderHandle);
        end);

    end
  );
end;


procedure TFolder.DoListFile(AFile: JSValue);
var
  fh: TFileSystemFileHandle;
begin
  SetLength(FFileArray, Length(FFileArray) + 1);

  fh := FFileArray[Length(FFileArray) - 1];

  asm
    fh.Kind = AFile.kind;
    fh.Name = AFile.name;
  end;

  FFileArray[Length(FFileArray) - 1] := fh;
end;

procedure TFolder.DoGetFile(AFileHandle: JSValue; AProc: TGetFileProc);
var
  LFileObject: TFileObject;
begin
  if Assigned(AProc) then
  begin
    asm
      LFileObject.Name = AFileHandle.name;
      LFileObject.Lastmodfieddate = AFileHandle.lastModifiedDate.toString();
      LFileObject.Size = AFileHandle.size;
      LFileObject.Fileobject = AFileHandle;
    end;
    AProc(LFileObject);
  end;
end;

procedure TFolder.DoGetFileHandle(AFileHandle: JSValue;
  AProc: TGetFileHandleProc);
begin
  if Assigned(AProc) then
  begin
    AProc(AFileHandle);
  end;
end;

procedure TFolder.DoGetFolder(AFolder: TFileSystemFileHandle);
begin
  FFolder := AFolder;
end;

procedure TFolder.DoGetFolderHandle(AFolder: TFileSystemFolderHandle; GetFolder: TGetFolderProc);
begin
  if Assigned(GetFolder) then
    GetFolder(AFolder);
end;

procedure TFolder.DoOpenFolder(AOpenFolder: TOpenFolderProc);
begin
  if Assigned(OnFolderOpen) then
    OnFolderOpen(Self);

  if Assigned(AOpenFolder) then
    AOpenFolder;
end;

function TFolder.FileHandle(const AName: string): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      GetFileHandle(AName, procedure(AFileHandle: JSValue)
        begin
          ASuccess(AFileHandle);
        end);
    end);
end;


function TFolder.FileObject(const AName: string): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      GetFile(ANAme, procedure(AFileObject: TFileObject)
        begin
          ASuccess(AFileObject);
        end);
    end);
end;


function TFolder.Folder(const AName: string): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      GetFolder(ANAme, procedure(AFolderHandle: TFileSystemFolderHandle)
        begin
          ASuccess(AFolderHandle);
        end);
    end);
end;

procedure TFolder.GetFile(const AName: string; GetFile: TGetFileProc);
begin
  asm
    async function getfile(afolder, aname, aproc)
    {
      const newFileHandle = await afolder.FFolder.getFileHandle(aname, { create: false });
      const file = await newFileHandle.getFile();
      afolder.DoGetFile(file, aproc);
    }
    getfile(this, AName, GetFile);
  end;
end;

procedure TFolder.GetFileHandle(const AName: string; GetFile: TGetFileHandleProc);
begin

  asm
    async function getfilehandle(afolder, aname, aproc)
    {
      const newFileHandle = await afolder.FFolder.getFileHandle(aname, { create: false });
      afolder.DoGetFileHandle(newFileHandle, aproc);
    }
    getfilehandle(this, AName, GetFile);
  end;
end;


procedure TFolder.GetFolder(const AName: string; GetFolder: TGetFolderProc);
begin
  asm
    async function getfolder(afolder, aname, aproc)
    {
      const newFolderHandle = await afolder.FFolder.getDirectoryHandle(aname, { create: false });
      afolder.DoGetFolderHandle(newFolderHandle, aproc);
    }
    getfolder(this, AName, GetFolder);
  end;
end;

procedure TFolder.Open(AOpenFolder: TOpenFolderProc);
var
  fs: TFileSystemFileHandle;
begin
  if FFolder = nil then
  begin
    asm
      async function showdir(afolder, aproc) {
        const dirHandle = await window.showDirectoryPicker();

        afolder.FFolder = dirHandle;

        for await (const entry of dirHandle.values()) {
          afolder.DoListFile(entry);
        }
        afolder.DoOpenFolder(aproc);
       }
       showdir(this, AOpenFolder);
    end;
  end
  else
  begin
    asm
      async function showdirhandle(afolder, aproc) {

        const dirHandle = afolder.FFolder;

        for await (const entry of dirHandle.values()) {
          afolder.DoListFile(entry);
        }
        afolder.DoOpenFolder(aproc);
       }
       showdirhandle(this, AOpenFolder);
    end;
  end;

  // fool the obfuscator
  if 1<0 then
  begin
    DoGetFile(nil,nil);
    DoListFile(nil);
    DoGetFolder(fs);
    DoOpenFolder(nil);
    DoGetFolderHandle(nil,nil);
  end;
end;

function TFolder.OpenFolder: TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      Open(procedure
        begin
          ASuccess(Files);
        end);
    end);
end;

procedure TFolder.Open;
begin
  Open(nil);
end;

{ TBinaryFile }

constructor TBinaryFile.Create(AOwner: TComponent);
begin
  inherited;
  FFilter := TFileFilter.Create(Self);

  if (1<0) then
  begin
    DoOpenFile(0, nil, nil);
    DoSaveFile(nil);
    DoSaveAsFile(0,nil);
  end;
end;

destructor TBinaryFile.Destroy;
begin
  FFilter.Free;
  inherited;
end;

procedure TBinaryFile.DoOpenFile(AHandle: JSValue; Value: TJSArrayBuffer; AOpenFile: TOpenBinaryFileProc);
begin
  FFileHandle := AHandle;
  asm
    this.FFileName = AHandle.name;
  end;

  FData := Value;
  if Assigned(OnFileOpen) then
    OnFileOpen(Self);
  if Assigned(AOpenFile) then
    AOpenFile(Value);
end;

procedure TBinaryFile.DoSaveAsFile(AHandle: JSValue; ASaveFile: TSaveFileProc);
begin
  FFileHandle := AHandle;
  SaveFile(ASaveFile);
end;

procedure TBinaryFile.DoSaveFile(ASaveFile: TSaveFileProc);
begin
  if Assigned(OnFileSave) then
    OnFileSave(Self);
  if Assigned(ASaveFile) then
    ASaveFile;
end;

function TBinaryFile.LoadStream(Stream: TCustomMemoryStream): TJSPromise;
var
  b: TBytes;
  l: longint;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      OpenFile(procedure(AValue: TJSArrayBuffer)
        begin
          b := TMemoryStream.MemoryToBytes(AValue);
          l := AValue.byteLength;
          Stream.Write(b,0,l);
          ASuccess(True);
        end);
    end);
end;

{$HINTS OFF}
procedure TBinaryFile.OpenFile(AOpenFile: TOpenBinaryFileProc);
var
  jo: TJSObject;

begin
  jo := FFilter.GetFilterObject;

  asm
    let fileHandle;
    async function asyncCall(afile) {
      [fileHandle] = await window.showOpenFilePicker(jo);
      const file = await fileHandle.getFile();
      const contents = await file.arrayBuffer();
      afile.DoOpenFile(fileHandle, contents, AOpenFile);
      }
    asyncCall(this);
  end;

  if 1<0 then
  begin
    DoOpenFile(nil, nil, nil);
    DoSaveFile(nil);
    DoSaveAsFile(nil,nil);
  end;
end;
{$HINTS ON}


function TBinaryFile.Open: TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      OpenFile(procedure(AValue: TJSArrayBuffer)
        begin
          ASuccess(AValue);
        end);
    end);
end;

function TBinaryFile.Open(AFileHandle: JSValue): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      OpenFile(AFileHandle, procedure(AValue: TJSArrayBuffer)
      begin
        ASuccess(AValue);
       end);
    end);
end;

procedure TBinaryFile.OpenFile(AFileHandle: JSValue; AOpenFile: TOpenBinaryFileProc);
begin
  asm
    async function asyncCall(afile) {
      const file = await AFileHandle.getFile();
      const contents = await file.arrayBuffer();
      afile.DoOpenFile(AFileHandle, contents, AOpenFile);
      }
    asyncCall(this);
  end;
end;


procedure TBinaryFile.OpenFile;
begin
  OpenFile(nil);
end;

function TBinaryFile.Save: TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      SaveFile(procedure
        begin
          ASuccess(true);
        end);
    end);
end;

function TBinaryFile.SaveAs: TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      SaveAsFile(procedure
        begin
          ASuccess(true);
        end);
    end);
end;

{$HINTS OFF}
procedure TBinaryFile.SaveAsFile(ASaveFile: TSaveFileProc);
var
  jo: TJSObject;
begin
  jo := Filter.GetFilterObject;
  asm
    async function getNewFileHandle(afile, aproc) {
    const handle = await window.showSaveFilePicker(jo);
    afile.DoSaveAsFile(handle, aproc);
    }
    getNewFileHandle(this, ASaveFile);
  end;
end;
{$HINTS ON}

procedure TBinaryFile.SaveAsFile;
begin
  SaveAsFile(nil);
end;

procedure TBinaryFile.SaveFile;
begin
  SaveFile(nil);
end;

procedure TBinaryFile.SaveFile(ASaveFile: TSaveFileProc);
begin
  if not Assigned(FFileHandle) then
    raise Exception.Create('File handle not assigned');
  asm
    async function writeFile(afile, fileHandle, contents, aproc) {
    // Create a FileSystemWritableFileStream to write to.
    const writable = await fileHandle.createWritable();
    // Write the contents of the file to the stream.
    await writable.write(contents);
    // Close the file and write the contents to disk.
    await writable.close();
    afile.DoSaveFile(aproc);
    }
    writeFile(this, this.FFileHandle, this.FData, ASaveFile);
  end;
end;



function TBinaryFile.SaveStream(Stream: TCustomMemoryStream): TJSPromise;
begin
  FData := Stream.Memory;

  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      SaveFile(procedure
        begin
          ASuccess(true);
        end);
    end);
end;

{ TFileFilterItem }

constructor TFileFilterItem.Create(ACollection: TCollection);
begin
  inherited;
  FExtensions := TStringList.Create;
end;

destructor TFileFilterItem.Destroy;
begin
  FExtensions.Free;
  inherited;
end;

function TFileFilterItem.GetExtensions: TStrings;
begin
  Result := TStrings(FExtensions);
end;

procedure TFileFilterItem.SetExtensions(const Value: TStrings);
begin
  FExtensions.Assign(Value);
end;

{ TFileFilter }

function TFileFilter.Add: TFileFilterItem;
begin
  Result := TFileFilterItem(inherited Add);
end;

function TFileFilter.Add(ADescription, AMIMEType,
  AExtensions: string): TFileFilterItem;
begin
  Result := Add;
  Result.Description := ADescription;
  Result.MIMEType := AMIMEType;
  Result.Extensions.CommaText  := AExtensions;
end;

constructor TFileFilter.Create(AOwner: TPersistent);
begin
  inherited Create(AOwner, TFileFilterItem);
end;

function TFileFilter.GetFilterObject: TJSObject;
var
  opts,all: string;
  jo: TJSObject;
begin
  if Count > 0 then
  begin
    if HasAllFiles then
      all := 'false'
    else
      all := 'true';

    opts := '{ "types":' + GetFilterString + ',"excludeAcceptAllOption": '+all+',"multiple": false }';
  end;

  if opts <> '' then
  asm
    jo = null;
    if (opts != "") {
      jo = JSON.parse(opts); }
  end;

  Result := jo;
end;

function TFileFilter.GetFilterString: string;
var
  i,j: integer;
  ext, mext: string;
begin
  Result := '';

  if Count > 0 then
  begin
    Result := '[';

    for i := 0 to Count - 1 do
    begin
      if i > 0 then
        Result := Result + ',';

      Result := Result + '{';

      if Items[i].Extensions.Count > 0 then
      begin
        ext := '';
        for j := 0 to Items[i].Extensions.Count - 1 do
        begin
          if j > 0 then
            ext := ext + ',';

          mext := Items[i].Extensions[j];
          if pos('*.', mext) > 0 then
            mext := copy(mext, 2, length(mext));

          if (mext = '*.*') or (mext = '.*') then
            continue;

          ext := ext + '"' + mext + '"';
        end;

        if ext <> '' then
          Result := Result + '"description": "' + Items[i].Description+ '", "accept": {"'+ Items[i].MIMEType+ '":['+ ext +']}';
      end;

      Result := Result + '}';
    end;

    Result := Result + ']';
  end;
end;

function TFileFilter.GetItems(Index: integer): TFileFilterItem;
begin
  Result := TFileFilterItem(inherited Items[Index]);
end;

function TFileFilter.HasAllFiles: boolean;
var
  i,j: integer;
begin
  Result := false;
  for i := 0 to Count - 1 do
  begin
    for j := 0 to Items[i].Extensions.Count - 1 do
    begin
      if (Items[i].Extensions[j] = '*') or (Items[i].Extensions[j] = '*.*') then
      begin
        Result := true;
        break;
      end;
    end;
  end;
end;

function TFileFilter.Insert(Index: integer): TFileFilterItem;
begin
  Result := TFileFilterItem(inherited Insert(Index));
end;

procedure TFileFilter.SetItems(Index: integer; const Value: TFileFilterItem);
begin
  inherited Items[Index] := Value;
end;

end.
